/**
 * Audit Logging Service for Security Events
 */

import { csrfService } from './csrf';

export interface AuditEvent {
  id?: string;
  userId?: string;
  sessionId?: string;
  eventType: AuditEventType;
  eventCategory: AuditEventCategory;
  description: string;
  metadata?: Record<string, unknown>;
  ipAddress?: string;
  userAgent?: string;
  timestamp?: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
}

export enum AuditEventType {
  // Authentication events
  LOGIN_SUCCESS = 'login_success',
  LOGIN_FAILURE = 'login_failure',
  LOGOUT = 'logout',
  PASSWORD_CHANGE = 'password_change',
  PASSWORD_RESET_REQUEST = 'password_reset_request',
  PASSWORD_RESET_SUCCESS = 'password_reset_success',
  EMAIL_VERIFICATION = 'email_verification',
  OAUTH_LOGIN = 'oauth_login',

  // Session events
  SESSION_START = 'session_start',
  SESSION_EXTEND = 'session_extend',
  SESSION_TIMEOUT = 'session_timeout',
  SESSION_TERMINATE = 'session_terminate',

  // Data access events
  PROFILE_VIEW = 'profile_view',
  PROFILE_UPDATE = 'profile_update',
  PORTFOLIO_VIEW = 'portfolio_view',
  PORTFOLIO_UPDATE = 'portfolio_update',
  DATA_EXPORT = 'data_export',
  DATA_DELETION = 'data_deletion',

  // Security events
  CSRF_TOKEN_MISMATCH = 'csrf_token_mismatch',
  RATE_LIMIT_EXCEEDED = 'rate_limit_exceeded',
  SUSPICIOUS_ACTIVITY = 'suspicious_activity',
  UNAUTHORIZED_ACCESS = 'unauthorized_access',
  INPUT_VALIDATION_FAILURE = 'input_validation_failure',

  // Privacy events
  PRIVACY_SETTINGS_UPDATE = 'privacy_settings_update',
  CONSENT_GIVEN = 'consent_given',
  CONSENT_WITHDRAWN = 'consent_withdrawn',

  // System events
  ERROR_OCCURRED = 'error_occurred',
  PERFORMANCE_ISSUE = 'performance_issue',
}

export enum AuditEventCategory {
  AUTHENTICATION = 'authentication',
  AUTHORIZATION = 'authorization',
  DATA_ACCESS = 'data_access',
  DATA_MODIFICATION = 'data_modification',
  PRIVACY = 'privacy',
  SECURITY = 'security',
  SYSTEM = 'system',
}

export class AuditService {
  private static instance: AuditService;
  private eventQueue: AuditEvent[] = [];
  // private isProcessing = false;
  // private batchSize = 10;
  // private flushInterval = 5000; // 5 seconds

  private constructor() {
    // this.startBatchProcessor();
    this.setupBeforeUnloadHandler();
  }

  public static getInstance(): AuditService {
    if (!AuditService.instance) {
      AuditService.instance = new AuditService();
    }
    return AuditService.instance;
  }

  // Log a security event
  public async logEvent(
    event: Omit<AuditEvent, 'timestamp' | 'ipAddress' | 'userAgent'>
  ): Promise<void> {
    const enrichedEvent: AuditEvent = {
      ...event,
      timestamp: new Date().toISOString(),
      ipAddress: await this.getClientIP(),
      userAgent: navigator.userAgent,
      sessionId: this.getSessionId(),
    };

    // Add to queue for batch processing
    this.eventQueue.push(enrichedEvent);

    // For critical events, flush immediately
    // if (event.severity === 'critical') {
    //   await this.flushEvents();
    // }
  }

  // Convenience methods for common events
  public async logLoginSuccess(
    userId: string,
    method: 'email' | 'oauth'
  ): Promise<void> {
    await this.logEvent({
      userId,
      eventType: AuditEventType.LOGIN_SUCCESS,
      eventCategory: AuditEventCategory.AUTHENTICATION,
      description: `User logged in successfully using ${method}`,
      metadata: { method },
      severity: 'low',
    });
  }

  public async logLoginFailure(email: string, reason: string): Promise<void> {
    await this.logEvent({
      eventType: AuditEventType.LOGIN_FAILURE,
      eventCategory: AuditEventCategory.AUTHENTICATION,
      description: `Login attempt failed: ${reason}`,
      metadata: { email, reason },
      severity: 'medium',
    });
  }

  public async logPasswordChange(userId: string): Promise<void> {
    await this.logEvent({
      userId,
      eventType: AuditEventType.PASSWORD_CHANGE,
      eventCategory: AuditEventCategory.AUTHENTICATION,
      description: 'User changed password',
      severity: 'medium',
    });
  }

  public async logDataExport(
    userId: string,
    exportType: string
  ): Promise<void> {
    await this.logEvent({
      userId,
      eventType: AuditEventType.DATA_EXPORT,
      eventCategory: AuditEventCategory.DATA_ACCESS,
      description: `User requested data export: ${exportType}`,
      metadata: { exportType },
      severity: 'medium',
    });
  }

  public async logDataDeletion(
    userId: string,
    deletionType: string
  ): Promise<void> {
    await this.logEvent({
      userId,
      eventType: AuditEventType.DATA_DELETION,
      eventCategory: AuditEventCategory.DATA_MODIFICATION,
      description: `User requested data deletion: ${deletionType}`,
      metadata: { deletionType },
      severity: 'high',
    });
  }

  public async logCSRFFailure(userId?: string): Promise<void> {
    await this.logEvent({
      userId,
      eventType: AuditEventType.CSRF_TOKEN_MISMATCH,
      eventCategory: AuditEventCategory.SECURITY,
      description: 'CSRF token mismatch detected',
      severity: 'high',
    });
  }

  public async logRateLimitExceeded(
    userId?: string,
    endpoint?: string
  ): Promise<void> {
    await this.logEvent({
      userId,
      eventType: AuditEventType.RATE_LIMIT_EXCEEDED,
      eventCategory: AuditEventCategory.SECURITY,
      description: 'Rate limit exceeded',
      metadata: { endpoint },
      severity: 'medium',
    });
  }

  public async logSuspiciousActivity(
    activity: string,
    userId?: string
  ): Promise<void> {
    await this.logEvent({
      userId,
      eventType: AuditEventType.SUSPICIOUS_ACTIVITY,
      eventCategory: AuditEventCategory.SECURITY,
      description: `Suspicious activity detected: ${activity}`,
      metadata: { activity },
      severity: 'high',
    });
  }

  public async logInputValidationFailure(
    field: string,
    value: string,
    userId?: string
  ): Promise<void> {
    await this.logEvent({
      userId,
      eventType: AuditEventType.INPUT_VALIDATION_FAILURE,
      eventCategory: AuditEventCategory.SECURITY,
      description: `Input validation failed for field: ${field}`,
      metadata: { field, value: value.substring(0, 100) }, // Truncate sensitive data
      severity: 'medium',
    });
  }

  public async logSessionTimeout(userId: string): Promise<void> {
    await this.logEvent({
      userId,
      eventType: AuditEventType.SESSION_TIMEOUT,
      eventCategory: AuditEventCategory.AUTHENTICATION,
      description: 'User session timed out',
      severity: 'low',
    });
  }

  public async logPrivacySettingsUpdate(
    userId: string,
    changes: Record<string, unknown>
  ): Promise<void> {
    await this.logEvent({
      userId,
      eventType: AuditEventType.PRIVACY_SETTINGS_UPDATE,
      eventCategory: AuditEventCategory.PRIVACY,
      description: 'User updated privacy settings',
      metadata: { changes },
      severity: 'low',
    });
  }

  // Flush events to server
  // private async flushEvents(): Promise<void> {
  //   if (this.isProcessing || this.eventQueue.length === 0) {
  //     return;
  //   }

  //   this.isProcessing = true;
  //   const eventsToSend = this.eventQueue.splice(0, this.batchSize);

  //   try {
  //     await csrfService.protectedFetch({
  //       url: '/api/audit/events',
  //       method: 'POST',
  //       data: { events: eventsToSend },
  //     });
  //   } catch (error) {
  //     // If sending fails, put events back in queue
  //     this.eventQueue.unshift(...eventsToSend);
  //     console.error('Failed to send audit events:', error);
  //   } finally {
  //     this.isProcessing = false;
  //   }
  // }

  // Start batch processor
  // private startBatchProcessor(): void {
  //   setInterval(() => {
  //     this.flushEvents();
  //   }, this.flushInterval);
  // }

  // Setup handler to flush events before page unload
  private setupBeforeUnloadHandler(): void {
    window.addEventListener('beforeunload', () => {
      // Use sendBeacon for reliable delivery during page unload
      // if (this.eventQueue.length > 0) {
      //   const events = this.eventQueue.splice(0);
      //   const data = JSON.stringify({ events });
      //   try {
      //     navigator.sendBeacon('/api/audit/events', data);
      //   } catch (error) {
      //     console.error('Failed to send audit events on unload:', error);
      //   }
      // }
    });
  }

  // Get client IP (best effort)
  private async getClientIP(): Promise<string | undefined> {
    try {
      // This would typically be handled by the server
      // For client-side, we can't reliably get the real IP
      return undefined;
    } catch {
      return undefined;
    }
  }

  // Get session ID
  private getSessionId(): string | undefined {
    return sessionStorage.getItem('session_id') || undefined;
  }

  // Get audit logs for current user (admin/debugging purposes)
  public async getUserAuditLogs(
    limit = 50,
    offset = 0,
    eventType?: AuditEventType
  ): Promise<{ events: AuditEvent[]; total: number }> {
    const params = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString(),
    });

    if (eventType) {
      params.append('eventType', eventType);
    }

    const response = await csrfService.protectedFetch({
      url: `/api/audit/events?${params.toString()}`,
      method: 'GET',
    });

    if (!response.ok) {
      throw new Error('Failed to fetch audit logs');
    }

    return response.json();
  }
}

// Export singleton instance
export const auditService = AuditService.getInstance();
